import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { fetchRates, WidgetApiError, type ProxyRatesResponse } from '../api';
import { strings } from '../messages';
import type { RatesOptions, WidgetRate } from '../types';

interface Props extends RatesOptions {
  apiUrl: string;
  emit: (event: string, payload: unknown) => void;
}

export function Rates({
  apiUrl,
  shipper,
  recipient,
  parcels,
  locale,
  onRateSelected,
  emit,
}: Props) {
  const t = strings(locale);
  const [data, setData] = useState<ProxyRatesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<WidgetRate | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const payload = useMemo(
    () => ({
      shipper: { postal_code: shipper.postalCode, city: shipper.city, country_code: shipper.countryCode },
      recipient: {
        postal_code: recipient.postalCode,
        city: recipient.city,
        country_code: recipient.countryCode,
      },
      parcels: parcels.map((p) => ({
        weight: p.weight,
        length: p.length,
        width: p.width,
        height: p.height,
        weight_unit: 'KG' as const,
        dimension_unit: 'CM' as const,
      })),
    }),
    [shipper, recipient, parcels],
  );

  async function compute() {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setError(null);
    setLoading(true);
    try {
      const resp = await fetchRates(apiUrl, payload, ac.signal);
      setData(resp);
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      const apiErr = err as WidgetApiError;
      setError(apiErr.message);
      emit('error', { code: apiErr.code ?? 'rates_failed', message: apiErr.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void compute();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload)]);

  return (
    <div class="tsw" role="region" aria-label={t.ratesTitle}>
      <p class="tsw-title">{t.ratesTitle}</p>
      <button
        class="tsw-button"
        type="button"
        onClick={() => void compute()}
        disabled={loading}
        style="margin-top:8px;"
      >
        {loading ? t.trackRefreshing : t.ratesCompute}
      </button>
      <div role="status" aria-live="polite" aria-atomic="true" style="min-height:1px;">
        {error && <p class="tsw-error" style="margin-top:12px;">{t.ratesError}</p>}
      </div>
      {data && (
        <ul style="list-style:none;padding:0;margin:12px 0 0;display:flex;flex-direction:column;gap:8px;">
          {(data.rates ?? []).map((r, i) => {
            const widgetRate: WidgetRate = {
              service: r.service ?? '',
              carrierName: r.carrier_name ?? '',
              totalCharge: r.total_charge ?? 0,
              currency: r.currency ?? 'TND',
              transitDays: r.transit_days,
              estimatedDelivery: r.estimated_delivery,
            };
            const active = chosen?.service === widgetRate.service && chosen.carrierName === widgetRate.carrierName;
            return (
              <li key={i} style="margin:0;padding:0;">
                <button
                  type="button"
                  class={`tsw-rate${active ? ' tsw-rate-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => {
                    setChosen(widgetRate);
                    onRateSelected?.(widgetRate);
                    emit('rate-selected', widgetRate);
                  }}
                >
                  <div>
                    <p style="margin:0;font-weight:600;">{widgetRate.service}</p>
                    <p class="tsw-muted tsw-mono">{widgetRate.carrierName}</p>
                  </div>
                  <div style="text-align:end;">
                    <p style="margin:0;">{`${widgetRate.totalCharge.toFixed(2)} ${widgetRate.currency}`}</p>
                    {widgetRate.transitDays != null && (
                      <p class="tsw-muted">{`${widgetRate.transitDays} d`}</p>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
          {(data.rates ?? []).length === 0 && <li class="tsw-muted">{t.ratesEmpty}</li>}
        </ul>
      )}
      <p class="tsw-muted" style="margin-top:12px;font-size:11px;">{t.poweredBy}</p>
    </div>
  );
}
